/** Signed content releases: manifest, bundle download, public key, adoption (spec section 13). */
import { z } from 'zod';
import { ContentReleaseIdSchema, DeviceIdSchema, TemplateIdSchema } from '../ids.js';
import { Ed25519JwkSchema, IsoDateTimeSchema, OkSchema, SemVerSchema, Sha256HexSchema, defineRoute } from './common.js';
import { ContentManifestSchema, ReleaseChannelSchema, TemplateTypeSchema } from './entities.js';

export const ContentManifestQuerySchema = z.object({
  channel: ReleaseChannelSchema.default('stable'),
  /** Current version on the device; lets the server answer 304 via ETag. */
  currentVersion: SemVerSchema.optional(),
  appVersion: SemVerSchema.optional(),
});
export type ContentManifestQuery = z.infer<typeof ContentManifestQuerySchema>;

export const ContentManifestResponseSchema = z.object({
  releaseId: ContentReleaseIdSchema,
  manifest: ContentManifestSchema,
  /** ETag header value echoed for conditional requests. */
  etag: z.string(),
});
export type ContentManifestResponse = z.infer<typeof ContentManifestResponseSchema>;

export const ContentBundleItemSchema = z.object({
  type: TemplateTypeSchema,
  id: TemplateIdSchema,
  version: z.number().int().positive(),
  digest: Sha256HexSchema,
  schemaVersion: z.number().int().positive(),
  /** Declarative JSON body (validated by domain/templates.ts per type). */
  body: z.unknown(),
});
export type ContentBundleItem = z.infer<typeof ContentBundleItemSchema>;

/** Content-addressed bundle. `manifest.digest` is sha256 over the canonical JSON of `items`. */
export const ContentBundleSchema = z.object({
  manifest: ContentManifestSchema,
  items: z.array(ContentBundleItemSchema),
});
export type ContentBundle = z.infer<typeof ContentBundleSchema>;

export const ContentPublicKeyResponseSchema = z.object({
  kid: z.string(),
  alg: z.literal('EdDSA'),
  jwk: Ed25519JwkSchema,
  /** Previous key kept during rotation so older bundles still verify. */
  previous: z.array(Ed25519JwkSchema),
});
export type ContentPublicKeyResponse = z.infer<typeof ContentPublicKeyResponseSchema>;

export const ContentAdoptRequestSchema = z
  .object({
    deviceId: DeviceIdSchema,
    releaseId: ContentReleaseIdSchema,
    version: SemVerSchema,
    digest: Sha256HexSchema,
    result: z.enum(['activated', 'rejected_signature', 'rejected_schema', 'rejected_compatibility']),
    activatedAt: IsoDateTimeSchema,
  })
  .strict();
export type ContentAdoptRequest = z.infer<typeof ContentAdoptRequestSchema>;

export const ContentReleaseParamsSchema = z.object({ releaseId: ContentReleaseIdSchema });

export const contentRoutes = {
  'content.manifest': defineRoute({
    id: 'content.manifest',
    method: 'GET',
    path: '/v1/content/manifest',
    auth: 'desktop',
    requestSchema: ContentManifestQuerySchema,
    responseSchema: ContentManifestResponseSchema,
    description: 'Active signed manifest for the channel and the caller cohort. Supports If-None-Match / ETag.',
  }),
  'content.bundle': defineRoute({
    id: 'content.bundle',
    method: 'GET',
    path: '/v1/content/bundles/:releaseId',
    auth: 'desktop',
    paramsSchema: ContentReleaseParamsSchema,
    requestSchema: z.object({}).loose(),
    responseSchema: ContentBundleSchema,
    description: 'Download a content bundle (JSON). Clients verify digest and signature before activation.',
  }),
  'content.publicKey': defineRoute({
    id: 'content.publicKey',
    method: 'GET',
    path: '/v1/content/public-key',
    auth: 'public',
    requestSchema: z.object({}).loose(),
    responseSchema: ContentPublicKeyResponseSchema,
    description: 'Content signing public key(s).',
  }),
  'content.adopt': defineRoute({
    id: 'content.adopt',
    method: 'POST',
    path: '/v1/content/adopt',
    auth: 'desktop',
    requestSchema: ContentAdoptRequestSchema,
    responseSchema: OkSchema,
    description: 'Report bundle activation or rejection for rollout tracking.',
  }),
} as const;
